import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { useNavigate } from "react-router-dom";
import { API } from "../lib/api";
import { Send, Sparkles, Loader2, Plus, MessageSquare } from "lucide-react";
import Hummingbird from "../components/brand/Hummingbird";

const SUGGESTIONS = [
  "Warum wiederholen sich dieselben Konflikte in meinen Beziehungen?",
  "Wie baue ich gesunden Selbstwert auf — Schritt für Schritt?",
  "Ich fühle mich leer, obwohl ich erfolgreich bin. Was nun?",
  "Wie führe ich ein bewusstes Gespräch mit meinem Partner?",
];

const AIChatPage = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [partial, setPartial] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth/login?redirect=/ai"); return; }
    loadSessions();
  }, [user, loading, nav]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, partial]);

  const loadSessions = async () => {
    const token = localStorage.getItem("sat_token");
    const res = await fetch(`${API}/ai/sessions`, { headers: { Authorization: `Bearer ${token}` } });
    setSessions(await res.json());
  };

  const openSession = async (sid) => {
    const token = localStorage.getItem("sat_token");
    const res = await fetch(`${API}/ai/sessions/${sid}`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await res.json();
    setSessionId(sid);
    setMessages(d.messages || []);
  };

  const newChat = () => { setSessionId(null); setMessages([]); setPartial(""); };

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    const userMsg = { id: "tmp-" + Date.now(), role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setStreaming(true);
    setPartial("");

    try {
      const token = localStorage.getItem("sat_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let collected = "";
      let newSessionId = sessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE events split by blank lines
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          if (p.startsWith("event: done")) {
            const m = p.match(/data:\s*(.+)/);
            if (m) newSessionId = m[1].trim();
          } else if (p.startsWith("event: error")) {
            const m = p.match(/data:\s*(.+)/);
            collected += "\n\n[Fehler: " + (m ? m[1] : "unknown") + "]";
            setPartial(collected);
          } else if (p.startsWith("data:")) {
            const m = p.match(/data:\s*([\s\S]+)/);
            if (m) {
              collected += m[1];
              setPartial(collected);
            }
          }
        }
      }
      // Finalize
      const aiMsg = { id: "tmp-ai-" + Date.now(), role: "assistant", content: collected, created_at: new Date().toISOString() };
      setMessages((m) => [...m, aiMsg]);
      setPartial("");
      if (!sessionId && newSessionId) {
        setSessionId(newSessionId);
        loadSessions();
      }
    } catch (e) {
      setPartial("[Fehler beim Streamen]");
    } finally {
      setStreaming(false);
    }
  };

  if (loading || !user) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  return (
    <div className="pt-24 min-h-screen" data-testid="ai-chat-page">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-10 py-6">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-3 space-y-3" data-testid="ai-sessions-list">
            <button onClick={newChat} className="btn-secondary text-sm w-full justify-center" data-testid="ai-new-chat">
              <Plus size={14} /> Neue Sitzung
            </button>
            <div className="overline mt-6">Deine Sitzungen</div>
            <div className="space-y-1.5">
              {sessions.length === 0 && <div className="text-xs text-white/45 p-2">Noch keine.</div>}
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSession(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition flex items-center gap-2 ${sessionId === s.id ? "bg-white/[0.06] text-white" : "text-white/65 hover:bg-white/[0.03]"}`}
                  data-testid={`ai-session-${s.id}`}
                >
                  <MessageSquare size={14} className="text-white/30 shrink-0" />
                  <span className="truncate">{s.title || "Sitzung"}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* Chat */}
          <main className="lg:col-span-9 glass rounded-3xl p-6 md:p-8 min-h-[70vh] flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <Hummingbird size={42} />
              <div>
                <div className="overline">SAT · AI</div>
                <div className="serif text-2xl">Bewusster Begleiter</div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-5" data-testid="ai-messages-container">
              {messages.length === 0 && !partial && (
                <div className="py-10">
                  <p className="serif italic text-2xl text-white/75 max-w-2xl">„Stell mir die Frage, die du sonst niemandem stellen würdest."</p>
                  <p className="text-white/55 mt-4 text-sm">Beispiele:</p>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2 max-w-3xl">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={i} onClick={() => send(s)} className="text-left text-sm text-white/80 px-4 py-3 rounded-xl border border-white/8 hover:bg-white/[0.04] transition" data-testid={`ai-suggestion-${i}`}>
                        <Sparkles size={12} className="text-[#D4AF37] inline mr-2" />{s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ai-msg-${m.role}`}>
                  <div className={`max-w-[80%] rounded-2xl px-5 py-3 whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#D4AF37] text-[#081328]" : "bg-white/[0.04] text-white/85 border border-white/8"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {partial && (
                <div className="flex gap-3" data-testid="ai-msg-streaming">
                  <div className="max-w-[80%] rounded-2xl px-5 py-3 bg-white/[0.04] text-white/85 border border-white/8 whitespace-pre-wrap leading-relaxed">
                    {partial}<span className="inline-block w-2 h-4 bg-[#D4AF37] ml-1 animate-pulse" />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="mt-6 flex gap-3" data-testid="ai-input-form">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Schreib, was dich gerade bewegt…"
                className="sat-input flex-1"
                disabled={streaming}
                data-testid="ai-input"
              />
              <button type="submit" disabled={streaming || !input.trim()} className="btn-primary" data-testid="ai-send">
                {streaming ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </button>
            </form>
            <p className="text-[10px] text-white/35 mt-3 text-center">SAT AI · powered by Claude. Bei akuten Krisen: Dargebotene Hand 143 (CH) · Telefonseelsorge 0800 111 0111 (DE).</p>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
